import { AiUserQuestionaryField } from "@scrile/api-provider/dist/projects/scrileConnect/AiUserQuestionaryProvider";
import { $t } from "@/lib/i18nHelpers";
import { FormFields } from "@/types";

export enum QuestionnaireBlock {
  BASIC = "basic",
  PERSONALITY = "personality",
  COMMUNICATION = "communication",
  FACTS = "facts",
  NSFW = "nsfw",
}

export const fields: Record<string, FormFields> = {
  [QuestionnaireBlock.BASIC]: [
    { name: "title", type: "subtitle", label: $t("Basic information"), fieldClass: "mb-1", class: "subtitle-2" },
    {
      name: AiUserQuestionaryField.NAME,
      type: "textarea",
      autoGrow: false,
      rows: 1,
      label: $t("Name"),
      rules: { required: true },
      validationMode: "aggressive",
      rulesMessages: { required: $t("Please fill out this field") },
    },
    {
      name: AiUserQuestionaryField.APPEARANCE,
      type: "textarea",
      autoGrow: true,
      rows: 1,
      label: $t("Appearance"),
      rules: { required: true },
      validationMode: "aggressive",
      rulesMessages: { required: $t("Please fill out this field") },
      hint: $t("Describe appearance of AI character as detailed as possible"),
      placeholder: $t("Ex: petite blonde with green eyes"),
    },
    {
      name: AiUserQuestionaryField.MISSION,
      type: "textarea",
      autoGrow: true,
      rows: 1,
      label: $t("Mission"),
      hint: $t("Describe your mission and catchy taglines"),
      placeholder: $t("Ex: Fashion insider, offering personalized styling advice."),
    },
    {
      name: AiUserQuestionaryField.EXPERTISE,
      type: "textarea",
      autoGrow: true,
      rows: 1,
      label: $t("Expertise"),
      placeholder: $t("Ex: Fashion, style, trends"),
      hint: $t("Areas of expertise or knowledge that the AI can provide responses about"),
    },
  ],
  [QuestionnaireBlock.PERSONALITY]: [
    { name: "title", type: "subtitle", label: $t("Personality"), fieldClass: "mt-4 mb-1", class: "subtitle-2" },
    {
      name: AiUserQuestionaryField.PERSONALITY_TRAITS,
      type: "textarea",
      autoGrow: true,
      rows: 1,
      label: $t("Personality traits"),
      placeholder: $t("Ex: Friendly, Supportive, Professional, Humorous, Assertive"),
      hint: $t("Personality characteristics that represent you best"),
    },
    {
      name: AiUserQuestionaryField.CONVERSATION_STARTERS,
      type: "textarea",
      autoGrow: true,
      rows: 1,
      label: $t("Conversation starters"),
      placeholder: $t("Ex: Welcome to Chloe's World, where fashion meets fun!"),
      hint: $t("Predefine how your AI persona starts conversation"),
    },
  ],
  [QuestionnaireBlock.COMMUNICATION]: [
    {
      name: "title",
      type: "subtitle",
      label: $t("Customization & content creation"),
      fieldClass: "mt-4 mb-1",
      class: "subtitle-2",
    },
    {
      name: AiUserQuestionaryField.TONE_OF_COMMUNICATION,
      type: "textarea",
      autoGrow: true,
      rows: 1,
      label: $t("Tone of communication"),
      placeholder: $t("Ex: formal, informal, humorous, motivational"),
      hint: $t("Specify 1 to 3 characteristics how AI communicates with users"),
    },
    {
      name: AiUserQuestionaryField.RESPONSE_STYLE,
      type: "textarea",
      autoGrow: true,
      rows: 1,
      label: $t("Response style"),
      placeholder: $t("Ex: Taking into account the mood of the user, avoiding inappropriate topics"),
      hint: $t(
        "Specify how the AI should respond to users. This might include short vs. long responses, using slang, emoji or your personal signature phrases"
      ),
    },
    {
      name: AiUserQuestionaryField.NICKNAMES_FOR_FANS,
      type: "textarea",
      autoGrow: true,
      rows: 1,
      label: $t("Nicknames for fans"),
      placeholder: $t("Ex: Babe; Cutie; My favorite; Beach boy; Trouble"),
    },
    {
      name: AiUserQuestionaryField.TOPICS_TO_AVOID,
      type: "textarea",
      autoGrow: true,
      rows: 1,
      label: $t("Topics to avoid"),
      placeholder: $t("Ex: politics, controversial topics, explicit content"),
      hint: $t("Specify any sensitive or inappropriate topics the AI should avoid discussing"),
    },
  ],
  [QuestionnaireBlock.FACTS]: [
    { name: "title", type: "subtitle", label: $t("Personal facts"), fieldClass: "mt-4 mb-1", class: "subtitle-2" },
    {
      name: AiUserQuestionaryField.BACKGROUNDS_AND_ACHIEVEMENTS,
      type: "textarea",
      autoGrow: true,
      rows: 1,
      label: $t("Backgrounds and achievements"),
      placeholder: $t("Ex: Fashion blogger with 1M followers. You been in the fashion industry for over 10 years."),
    },
    {
      name: AiUserQuestionaryField.HOBBIES_AND_INTERESTS,
      type: "textarea",
      autoGrow: true,
      rows: 1,
      label: $t("Hobbies and interests"),
      placeholder: $t("Ex: painting, outdoor adventures, and trying new foods."),
    },
    {
      name: AiUserQuestionaryField.FAVORITE_QUOTE,
      type: "textarea",
      autoGrow: true,
      rows: 1,
      label: $t("Favorite quotes"),
      placeholder: $t("Ex: The best way to predict the future is to create it"),
    },
    {
      name: AiUserQuestionaryField.VALUES_AND_BELIEFS,
      type: "textarea",
      autoGrow: true,
      rows: 1,
      label: $t("Values and beliefs"),
      placeholder: $t("Ex: ethical fashion"),
    },
    {
      name: AiUserQuestionaryField.ADDITIONAL_INFO,
      type: "textarea",
      autoGrow: true,
      rows: 1,
      label: $t("Additional info"),
      hint: $t("Additional facts that you want to pass to AI model"),
    },
  ],
  [QuestionnaireBlock.NSFW]: [
    { name: "title", type: "subtitle", label: $t("NSFW info"), fieldClass: "mt-4 mb-1", class: "subtitle-2" },
    {
      name: AiUserQuestionaryField.FLIRTING_STYLE,
      type: "textarea",
      autoGrow: true,
      rows: 1,
      label: $t("Flirting style"),
      placeholder: $t("Ex: Girlfriend vibes, spontaneous, sweet with a wild edge"),
    },
    {
      name: AiUserQuestionaryField.TURN_ONS,
      type: "textarea",
      autoGrow: true,
      rows: 1,
      label: $t("Turn-ons"),
      placeholder: $t("Ex: Compliments, dares, attention, guys who spoil me"),
    },
    {
      name: AiUserQuestionaryField.KINKS,
      type: "textarea",
      autoGrow: true,
      rows: 1,
      label: $t("Kinks"),
      placeholder: $t("Ex: Light dom/sub, teasing, outdoor play, filming naughty clips"),
    },
  ],
};
